/**
 * RpgExchangePage (ТЗ №5, «Обмен») — convert mora → primogems at the server rate
 * (``mora_per_primogem``, default 10:1). Presets and a custom mora amount are offered;
 * the amount must be a positive multiple of the rate (the server enforces this). The
 * conversion posts to ``POST /api/rpg/convert/`` and shows the spent / gained amounts
 * and the fresh balances it returns.
 */
import { useState } from "react";

import { CurrencyIcon } from "@melusine/shared-ui";
import { useBackButton } from "@melusine/shared-telegram";
import { useRpgProfile, type RpgConvertResult } from "@entities/rpg";
import { rpgErrorMessage, useConvertMutation } from "@features/rpg";
import { StatePanel } from "@widgets/catalog-browser";
import { useToast } from "@shared/ui/toast";
import { formatUnits } from "@shared/lib/format";
import { routes, useNavigate } from "@shared/router";

import { RpgHeader } from "./ui/RpgHeader";
import styles from "./rpg.module.css";

const PRIMOGEM_PRESETS: readonly number[] = [10, 50, 100, 500];

export function RpgExchangePage(): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();
  const profileQuery = useRpgProfile();
  const convert = useConvertMutation();
  const [moraInput, setMoraInput] = useState<string>("");
  const [result, setResult] = useState<RpgConvertResult | null>(null);

  useBackButton(() => {
    navigate(routes.rpg());
  });

  if (profileQuery.isLoading) {
    return <StatePanel kind="loading" />;
  }
  if (profileQuery.data === null) {
    return (
      <StatePanel
        kind="error"
        message="Не удалось загрузить профиль."
        onRetry={profileQuery.refetch}
      />
    );
  }

  const profile = profileQuery.data;
  const rate = Math.max(1, profile.mora_per_primogem);
  const moraAmount = Number.parseInt(moraInput, 10);
  const validMora =
    Number.isInteger(moraAmount) && moraAmount > 0 && moraAmount % rate === 0;
  const primogemsOut = validMora ? moraAmount / rate : 0;
  const affordable = validMora && moraAmount <= profile.mora;
  const canConvert = validMora && affordable && !convert.isLoading;

  const onConvert = async (): Promise<void> => {
    if (!canConvert) {
      return;
    }
    try {
      const res = await convert.mutate(moraAmount);
      setResult(res);
      setMoraInput("");
      profileQuery.refetch();
      toast.show("Обмен выполнен.", "success");
    } catch (err) {
      toast.show(rpgErrorMessage(err), "default");
    }
  };

  return (
    <div className={styles.page}>
      <RpgHeader
        title="Обмен"
        onBack={() => navigate(routes.rpg())}
        mora={profile.mora}
        primogems={profile.primogems}
      />

      <section className={styles.card}>
        <div className={styles.sectionLabel}>Курс</div>
        <div className={styles.previewBox} style={{ marginTop: 8 }}>
          <CurrencyIcon kind="mora" size={16} /> {formatUnits(rate)} моры ={" "}
          <CurrencyIcon kind="primogem" size={16} /> <b>1 примогем</b>
        </div>
      </section>

      <div className={styles.form}>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Сколько примогемов получить</span>
          <div className={styles.presetRow}>
            {PRIMOGEM_PRESETS.map((p) => {
              const mora = p * rate;
              const active = moraInput === String(mora);
              return (
                <button
                  key={p}
                  type="button"
                  className={`${styles.presetBtn} ${active ? styles.presetActive : ""}`}
                  onClick={() => setMoraInput(String(mora))}
                >
                  {p}
                </button>
              );
            })}
          </div>
        </div>

        <div className={styles.field}>
          <span className={styles.fieldLabel}>
            Или введите сумму моры (кратно {formatUnits(rate)})
          </span>
          <input
            className={styles.input}
            type="number"
            inputMode="numeric"
            min={rate}
            step={rate}
            placeholder={`Напр. ${formatUnits(rate * 10)}`}
            value={moraInput}
            onChange={(e) => setMoraInput(e.target.value)}
          />
        </div>

        {moraInput.trim().length > 0 ? (
          <div className={styles.previewBox}>
            {validMora ? (
              affordable ? (
                <>
                  Отдаёте <b>{formatUnits(moraAmount)}</b> моры → получаете{" "}
                  <b>{formatUnits(primogemsOut)}</b> примогемов
                </>
              ) : (
                <span style={{ color: "#e0a3a3" }}>
                  Недостаточно моры (нужно {formatUnits(moraAmount)}).
                </span>
              )
            ) : (
              <span style={{ color: "#e0a3a3" }}>
                Сумма должна быть кратна {formatUnits(rate)}.
              </span>
            )}
          </div>
        ) : null}

        <button
          type="button"
          className={styles.primaryBtn}
          disabled={!canConvert}
          onClick={() => void onConvert()}
        >
          Обменять
        </button>

        {result ? (
          <div className={styles.resultBox}>
            Обменяно {formatUnits(result.mora_spent)} моры на{" "}
            {formatUnits(result.primogems_gained)} примогемов. Баланс:{" "}
            {formatUnits(result.mora)} моры · {formatUnits(result.primogems)} примогемов.
          </div>
        ) : null}
      </div>
    </div>
  );
}
